import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Stack,
  Typography,
  Switch,
  FormControlLabel,
  Grid,
} from '@mui/material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import type {
  ReminderWithRelations,
  CreateReminderRequest,
  UpdateReminderRequest,
  RecurrenceFrequency,
} from '../../types/reminder';

interface ReminderFormProps {
  reminder?: ReminderWithRelations | null;
  onChange: (data: CreateReminderRequest | UpdateReminderRequest) => void;
  tasks?: Array<{ _id: string; title: string }>;
  projects?: Array<{ _id: string; name: string }>;
  clients?: Array<{ _id: string; firstName: string; lastName: string }>;
}

export const ReminderForm: React.FC<ReminderFormProps> = ({
  reminder,
  onChange,
  tasks = [],
  projects = [],
  clients = [],
}) => {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [remindAt, setRemindAt] = useState<Date | null>(null);
  const [isRecurring, setIsRecurring] = useState(false);
  const [frequency, setFrequency] = useState<RecurrenceFrequency>('daily');
  const [interval, setInterval] = useState(1);
  const [taskId, setTaskId] = useState('');
  const [projectId, setProjectId] = useState('');
  const [clientId, setClientId] = useState('');

  // Initialize form
  useEffect(() => {
    if (reminder) {
      setTitle(reminder.title);
      setDescription(reminder.description || '');
      setRemindAt(new Date(reminder.remindAt));
      setIsRecurring(reminder.reminderType === 'recurring');
      if (reminder.recurrence) {
        setFrequency(reminder.recurrence.frequency);
        setInterval(reminder.recurrence.interval);
      }
      setTaskId(reminder.taskId?._id || '');
      setProjectId(reminder.projectId?._id || '');
      setClientId(reminder.clientId?._id || '');
    } else {
      // Default to 1 hour from now
      setRemindAt(new Date(Date.now() + 60 * 60 * 1000));
    }
  }, [reminder]);

  // Emit changes
  useEffect(() => {
    const data: CreateReminderRequest | UpdateReminderRequest = {
      title: title.trim(),
      description: description || undefined,
      remindAt: remindAt?.toISOString() || '',
      reminderType: isRecurring ? 'recurring' : 'one_time',
      taskId: taskId || undefined,
      projectId: projectId || undefined,
      clientId: clientId || undefined,
    };

    if (isRecurring) {
      data.recurrence = {
        frequency,
        interval,
      };
    }

    onChange(data);
  }, [title, description, remindAt, isRecurring, frequency, interval, taskId, projectId, clientId, onChange]);

  return (
    <Stack spacing={3}>
      {/* Title */}
      <TextField
        label="Reminder Title"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        fullWidth
        required
        placeholder="What do you want to be reminded about?"
      />

      {/* Description */}
      <TextField
        label="Description (optional)"
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        multiline
        rows={2}
        fullWidth
        placeholder="Add more details..."
      />

      {/* Date/Time */}
      <DateTimePicker
        label="Remind At"
        value={remindAt}
        onChange={(date) => setRemindAt(date)}
        slotProps={{
          textField: { fullWidth: true, required: true },
        }}
        minDateTime={new Date()}
      />

      {/* Recurring Toggle */}
      <FormControlLabel
        control={
          <Switch
            checked={isRecurring}
            onChange={(e) => setIsRecurring(e.target.checked)}
          />
        }
        label="Recurring reminder"
      />

      {/* Recurrence Options */}
      {isRecurring && (
        <Box sx={{ pl: 2, borderLeft: 2, borderColor: 'primary.main' }}>
          <Typography variant="subtitle2" gutterBottom>
            Repeat Settings
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <FormControl fullWidth size="small">
                <InputLabel>Frequency</InputLabel>
                <Select
                  value={frequency}
                  onChange={(e) => setFrequency(e.target.value as RecurrenceFrequency)}
                  label="Frequency"
                >
                  <MenuItem value="daily">Daily</MenuItem>
                  <MenuItem value="weekly">Weekly</MenuItem>
                  <MenuItem value="monthly">Monthly</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Every"
                type="number"
                size="small"
                value={interval}
                onChange={(e) => setInterval(Math.max(1, parseInt(e.target.value) || 1))}
                fullWidth
                InputProps={{
                  endAdornment: (
                    <Typography variant="caption" color="text.secondary">
                      {frequency === 'daily' ? 'day(s)' : frequency === 'weekly' ? 'week(s)' : 'month(s)'}
                    </Typography>
                  ),
                }}
              />
            </Grid>
          </Grid>
        </Box>
      )}

      {/* Link to Task */}
      {tasks.length > 0 && (
        <FormControl fullWidth>
          <InputLabel>Link to Task (optional)</InputLabel>
          <Select
            value={taskId}
            onChange={(e) => setTaskId(e.target.value)}
            label="Link to Task (optional)"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {tasks.map((task) => (
              <MenuItem key={task._id} value={task._id}>
                {task.title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Link to Project */}
      {projects.length > 0 && (
        <FormControl fullWidth>
          <InputLabel>Link to Project (optional)</InputLabel>
          <Select
            value={projectId}
            onChange={(e) => setProjectId(e.target.value)}
            label="Link to Project (optional)"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {projects.map((project) => (
              <MenuItem key={project._id} value={project._id}>
                {project.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {/* Link to Client */}
      {clients.length > 0 && (
        <FormControl fullWidth>
          <InputLabel>Link to Client (optional)</InputLabel>
          <Select
            value={clientId}
            onChange={(e) => setClientId(e.target.value)}
            label="Link to Client (optional)"
          >
            <MenuItem value="">
              <em>None</em>
            </MenuItem>
            {clients.map((client) => (
              <MenuItem key={client._id} value={client._id}>
                {client.firstName} {client.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    </Stack>
  );
};

export default ReminderForm;
